/* BankRulesStatusBar — surfaces active bank categorization rules at the
 * top of the Banking page so users always know which auto-rules are in
 * effect. Auto-rules silently categorize incoming transactions on every
 * bank sync, which can feel surprising the first time it happens. This
 * bar makes them visible and gives a one-click jump to the rules page.
 *
 * Renders nothing when no rules exist (avoids visual noise for brand-new
 * workspaces).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, ChevronRight } from 'lucide-react';
import { apiFetch } from '../../fetchInterceptor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BankRulesStatusBar({ workspaceId }) {
  const [rules, setRules] = useState([]);

  useEffect(() => {
    if (!workspaceId) return;
    let alive = true;
    (async () => {
      try {
        const r = await apiFetch(
          `${API_URL}/api/ai-buck/rules?workspace_id=${workspaceId}`,
        );
        if (!r.ok) return;
        const data = await r.json();
        if (alive) setRules(Array.isArray(data) ? data : []);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [workspaceId]);

  if (!rules.length) return null;

  // Up to two example rules — keeps the banner short on screens with
  // many rules.
  const sample = rules.slice(0, 2);
  const remaining = rules.length - sample.length;

  return (
    <div
      data-testid="bank-rules-status-bar"
      className="flex items-center justify-between p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/15 border border-emerald-200/70 dark:border-emerald-800/40"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="p-2 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex-shrink-0">
          <ShieldCheck className="w-4 h-4 text-emerald-700 dark:text-emerald-300" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium">
            <span data-testid="bank-rules-count">{rules.length}</span>{' '}
            active categorization rule{rules.length === 1 ? '' : 's'} —{' '}
            future matching transactions will be{' '}
            <strong>auto-categorized on bank sync</strong>.
          </p>
          <p className="text-xs text-muted-foreground truncate" data-testid="bank-rules-samples">
            {sample.map((r) => (
              <span key={r.rule_id} className="mr-2">
                <span className="font-mono">{r.name}</span>
                {r.target_account_name && (
                  <> → {r.target_account_name}</>
                )}
              </span>
            ))}
            {remaining > 0 && <span>+{remaining} more</span>}
          </p>
        </div>
      </div>
      <Link
        to="/ai-buck?tab=rules"
        data-testid="bank-rules-manage-link"
        className="flex-shrink-0 inline-flex items-center text-sm font-semibold text-emerald-700 dark:text-emerald-300 hover:text-emerald-800 dark:hover:text-emerald-200"
      >
        Manage rules
        <ChevronRight className="w-4 h-4 ml-1" />
      </Link>
    </div>
  );
}
